import { useState, useEffect } from 'react';
import { getMostViewed, createMostViewed, updateMostViewed, deleteMostViewed } from '@/services/promotionService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function MostViewedList() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [productIdStr, setProductIdStr] = useState('');
  const [viewsStr, setViewsStr] = useState('1');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editViewsStr, setEditViewsStr] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getMostViewed();
      setItems(data);
    } catch (e) {
      toast.error('Failed to load items.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleAdd = async () => {
    const productId = parseInt(productIdStr);
    const views = parseInt(viewsStr);
    if (isNaN(productId) || isNaN(views)) {
      toast.error('Please enter valid numbers.');
      return;
    }
    try {
      await createMostViewed(productId, views);
      toast.success('Added successfully.');
      setProductIdStr('');
      setViewsStr('1');
      fetchItems();
    } catch (e) {
      toast.error('Failed to add. Make sure the product exists.');
    }
  };

  const handleSaveEdit = async (id: number) => {
    const views = parseInt(editViewsStr);
    if (isNaN(views)) {
      toast.error('Please enter a valid number for views.');
      return;
    }
    try {
      await updateMostViewed(id, views);
      toast.success('Updated successfully.');
      setEditingId(null);
      fetchItems();
    } catch (e) {
      toast.error('Failed to update.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMostViewed(id);
      toast.success('Deleted successfully.');
      fetchItems();
    } catch (e) {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Most Viewed</h1>
        <p className="text-muted-foreground">Manage the most viewed products.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add Most Viewed Product</CardTitle>
          <CardDescription>Enter a Product ID and their initial view count</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4 items-center">
          <Input 
            placeholder="Product ID (e.g. 12)" 
            value={productIdStr} 
            onChange={(e) => setProductIdStr(e.target.value)} 
          />
          <Input 
            placeholder="Views (e.g. 100)" 
            type="number"
            value={viewsStr} 
            onChange={(e) => setViewsStr(e.target.value)} 
          />
          <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Most Viewed</CardTitle>
          <CardDescription>{loading ? 'Loading...' : `Showing ${items.length} items`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Product ID</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Product details</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.product_id}</TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <div className="flex items-center gap-2">
                        <Input 
                          className="w-24 h-8"
                          type="number"
                          value={editViewsStr} 
                          onChange={(e) => setEditViewsStr(e.target.value)} 
                        />
                        <Button size="sm" onClick={() => handleSaveEdit(item.id)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <span>{item.views}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.products ? (
                      <div className="flex items-center gap-3">
                        {item.products.image && (
                          <img src={item.products.image} alt="pic" className="w-8 h-8 rounded object-cover" />
                        )}
                        <span>{item.products.name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground italic">No data</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => {
                      setEditingId(item.id);
                      setEditViewsStr(item.views?.toString() || '0');
                    }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No most viewed items found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
